import { ComponentProps, ISize } from '@/types/cms';
import { FC } from 'react';
import Container from '../Layout/container';
import ComplexSkeleton from './skeleton';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';
import useLazyData from '@/hooks/useLazyData';
import { getCroppedImageUrl } from '@/components/common/CroppedImage';
import Size from '../../types/size';

const ComplexTitle: FC<ComponentProps> = props => {
  const { properties, cmsComponentKey, componentPosition } = props;
  const titleStyle = properties?.title_style && JSON.parse(properties?.title_style);
  const subTitleStyle = properties?.sub_title_style && JSON.parse(properties?.sub_title_style);

  const { ref, show } = useLazyData({
    threshold: 0.01,
    rootMargin: '15px 0px 0px 0px'
  });

  if (!show) {
    return <ComplexSkeleton viewRef={ref} />;
  }

  return (
    <Container
      cmsComponentKey={cmsComponentKey}
      properties={properties}
      className={twMerge(
        classNames('overflow-hidden', {
          'mx-2.5': properties?.horizontal_margin === Size.small,
          'mx-5': properties?.horizontal_margin === Size.middle,
          'mx-10': properties?.horizontal_margin === Size.large
        })
      )}>
      <div className="flex flex-col">
        <div className="flex justify-between">
          <div className="flex flex-1 flex-col">
            <div
              className={twMerge(
                classNames(
                  'enki-body-2xs text-sky-900 line-clamp-1',
                  {
                    '!text-[14px]': titleStyle?.font_size === (ISize.small as string)
                  },
                  {
                    '!text-[17px]': titleStyle?.font_size === (ISize.middle as string)
                  },
                  {
                    '!text-[20px]': titleStyle?.font_size === (ISize.large as string)
                  }
                )
              )}
              style={{
                textAlign: titleStyle?.align,
                color: titleStyle?.color
              }}>
              {properties?.title}
            </div>
            {!!properties?.sub_title && (
              <div
                className={twMerge(
                  classNames(
                    'enki-heading-xl !leading-[1.3] text-surface-100-fg-default mt-1.5 line-clamp-2',
                    {
                      '!text-[14px]': subTitleStyle?.font_size === (ISize.small as string)
                    },
                    {
                      '!text-[17px]': subTitleStyle?.font_size === (ISize.middle as string)
                    },
                    {
                      '!text-[20px]': subTitleStyle?.font_size === (ISize.large as string)
                    }
                  )
                )}
                style={{
                  textAlign: subTitleStyle?.align,
                  color: subTitleStyle?.color
                }}>
                {properties?.sub_title}
              </div>
            )}
          </div>
          {!!properties?.icon && (
            <div className="w-[50px] h-[50px] flex-none ml-1.5">
              <img
                className="w-[50px] h-[50px]"
                src={getCroppedImageUrl(properties?.icon, {
                  renderHeight: 25,
                  renderWidth: 25,
                  devicePixelRatio: 2
                })}
                alt="icon"
              />
            </div>
          )}
        </div>
        {!!properties?.html_text && (
          <div
            className="enki-body-base-medium mt-2.5 text-surface-100-fg-minor"
            dangerouslySetInnerHTML={{ __html: properties?.html_text }}></div>
        )}
      </div>
    </Container>
  );
};

export default ComplexTitle;
